/**
 * Authentication Helpers
 *
 * Provides authentication utilities for login, registration, logout,
 * and user profile management
 */

import type { SupabaseClient, User, Session } from '@supabase/supabase-js'
import type { Database, Tables } from './database.types'

export interface AuthResult {
  success: boolean
  user?: User
  session?: Session
  error?: string
  errorCode?: string
}

export type UserProfile = Tables<'users'>

export interface RegisterMetadata {
  full_name: string
  phone_number?: string
  user_type?: 'customer' | 'restaurant_owner'
}

/**
 * Login with email and password
 *
 * @param client - Supabase client instance
 * @param email - User email
 * @param password - User password
 * @returns AuthResult with user and session data
 */
export const loginWithPassword = async (
  client: SupabaseClient<Database>,
  email: string,
  password: string
): Promise<AuthResult> => {
  const { data, error } = await client.auth.signInWithPassword({
    email,
    password
  })

  if (error) {
    return {
      success: false,
      error: error.message,
      errorCode: error.code
    }
  }

  return {
    success: true,
    user: data.user ?? undefined,
    session: data.session ?? undefined
  }
}

/**
 * Register a new user
 *
 * Creates a new user account with metadata. The user will receive
 * a confirmation email if email confirmation is enabled.
 *
 * @param client - Supabase client instance
 * @param email - User email
 * @param password - User password
 * @param metadata - Additional user metadata
 * @returns AuthResult with user and session data
 */
export const registerUser = async (
  client: SupabaseClient<Database>,
  email: string,
  password: string,
  metadata: RegisterMetadata
): Promise<AuthResult> => {
  const { data, error } = await client.auth.signUp({
    email,
    password,
    options: {
      data: metadata
    }
  })

  if (error) {
    return {
      success: false,
      error: error.message,
      errorCode: error.code
    }
  }

  return {
    success: true,
    user: data.user ?? undefined,
    session: data.session ?? undefined
  }
}

/**
 * Logout the current user
 *
 * @param client - Supabase client instance
 */
export const logout = async (client: SupabaseClient<Database>): Promise<void> => {
  await client.auth.signOut()
}

/**
 * Get current user profile from database
 *
 * @param client - Supabase client instance
 * @param userId - User ID
 * @returns UserProfile or null if not found
 */
export const getUserProfile = async (
  client: SupabaseClient<Database>,
  userId: string
): Promise<UserProfile | null> => {
  console.warn('[getUserProfile] Starting query for userId:', userId)

  try {
    console.warn('[getUserProfile] Building query...')
    const query = client
      .from('users')
      .select('*')
      .eq('id', userId)
      .single()

    console.warn('[getUserProfile] Awaiting query result...')
    const { data, error } = await query
    console.warn('[getUserProfile] Query completed. Error:', error?.message || 'none', 'Data:', data ? 'found' : 'null')

    if (error || !data) {
      console.warn('[getUserProfile] Returning null due to error or missing data')
      return null
    }

    console.warn('[getUserProfile] Returning user profile')
    return data
  } catch (err) {
    console.warn('[getUserProfile] EXCEPTION:', err)
    return null
  }
}

/**
 * Update user profile
 *
 * @param client - Supabase client instance
 * @param userId - User ID
 * @param updates - Partial user profile updates
 * @returns Updated UserProfile or null on error
 */
export const updateUserProfile = async (
  client: SupabaseClient<Database>,
  userId: string,
  updates: Partial<Omit<UserProfile, 'id' | 'created_at' | 'updated_at'>>
): Promise<UserProfile | null> => {
  const { data, error } = await client
    .from('users')
    // @ts-expect-error Supabase client generic inference - update expects 'never' due to table type resolution
    .update(updates)
    .eq('id', userId)
    .select()
    .single()

  if (error || !data) {
    return null
  }

  return data
}

/**
 * Reset password for user
 *
 * Sends a password reset email to the user
 *
 * @param client - Supabase client instance
 * @param email - User email
 * @returns Success status
 */
export const resetPassword = async (
  client: SupabaseClient<Database>,
  email: string
): Promise<{ success: boolean; error?: string }> => {
  const { error } = await client.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`
  })

  if (error) {
    return {
      success: false,
      error: error.message
    }
  }

  return { success: true }
}

/**
 * Update user password
 *
 * @param client - Supabase client instance
 * @param newPassword - New password
 * @returns Success status
 */
export const updatePassword = async (
  client: SupabaseClient<Database>,
  newPassword: string
): Promise<{ success: boolean; error?: string }> => {
  const { error } = await client.auth.updateUser({
    password: newPassword
  })

  if (error) {
    return {
      success: false,
      error: error.message
    }
  }

  return { success: true }
}

/**
 * Listen for auth state changes
 *
 * @param client - Supabase client instance
 * @param callback - Callback function called when session changes
 * @returns Unsubscribe function
 */
export const onAuthStateChange = (
  client: SupabaseClient<Database>,
  callback: (session: Session | null) => void
) => {
  const { data: { subscription } } = client.auth.onAuthStateChange((_event, session) => {
    callback(session)
  })

  // Return unsubscribe function
  return () => subscription.unsubscribe()
}

/**
 * Get current session
 *
 * @param client - Supabase client instance
 * @returns Current session or null
 */
export const getSession = async (
  client: SupabaseClient<Database>
): Promise<Session | null> => {
  const { data: { session } } = await client.auth.getSession()
  return session
}

/**
 * Get current user
 *
 * @param client - Supabase client instance
 * @returns Current user or null
 */
export const getCurrentUser = async (
  client: SupabaseClient<Database>
): Promise<User | null> => {
  const { data: { user } } = await client.auth.getUser()
  return user
}
